import ms, { type StringValue } from "ms";
import { z } from "zod";
import { defineStepConfig } from "../registry";
import { SystemEvent } from "@erp/shared-types";

// Input schema - no inputs, this is an entry point
const inputSchema = z.never();

const EVENTS_WITH_EXTRA_CONFIG = [SystemEvent.EMAIL_ROUTED_EMAIL_RULE] as const;

const FALLBACK_EVENT_TYPES = Object.values(SystemEvent).filter(
  (
    eventType,
  ): eventType is Exclude<
    SystemEvent,
    (typeof EVENTS_WITH_EXTRA_CONFIG)[number]
  > =>
    !EVENTS_WITH_EXTRA_CONFIG.includes(
      eventType as (typeof EVENTS_WITH_EXTRA_CONFIG)[number],
    ),
);

const fallbackEventTypeSchema = z.enum(
  FALLBACK_EVENT_TYPES as [string, ...string[]],
  {
    error: () =>
      "Event type must be one of the following: " +
      FALLBACK_EVENT_TYPES.join(", "),
  },
);

const isMsParseable = (val: string): boolean => {
  try {
    return ms(val as StringValue) != null;
  } catch {
    return false;
  }
};

const workflowExecutionTimeoutSchema = z
  .string()
  .optional()
  .refine((val) => val === undefined || isMsParseable(val), {
    message:
      "Must be a ms-parseable duration string (e.g., '2 hours', '30 minutes', '90s')",
  })
  .describe(
    "Max workflow execution time as ms-parseable string (e.g., '2 hours'). Defaults to 1 hour.",
  );

const configSchema = z.discriminatedUnion("eventType", [
  z.object({
    eventType: z.literal(SystemEvent.EMAIL_ROUTED_EMAIL_RULE),
    emailRuleOptionId: z.uuid(),
    skipSelfSent: z
      .boolean()
      .optional()
      .describe(
        "When true, do not trigger this workflow for outbound emails that loop back into our own inbox (i.e. emails the system sent that re-appeared as inbound). Does not affect replies from external parties. Defaults to false.",
      ),
    coalesceByMessageId: z
      .boolean()
      .optional()
      .describe(
        "When true, start at most one run per real email rather than one per mailbox delivery. The ingest worker writes one inbound email per watched mailbox, so a supplier replying to several of our addresses triggers this workflow once per copy of the SAME message. Enable only when the workflow's subject is the message; a workflow that files, routes or acknowledges per mailbox needs its run per copy. Best-effort — two copies arriving together can still both start, so downstream dedupe stays required. Defaults to false.",
      ),
    workflowExecutionTimeout: workflowExecutionTimeoutSchema,
  }),
  // all event types without other config
  z.object({
    eventType: fallbackEventTypeSchema,
    workflowExecutionTimeout: workflowExecutionTimeoutSchema,
    // Additional event types that ALSO trigger this workflow (OR with
    // eventType). One trigger node can OR several events when they share a
    // payload shape; a workflow may also carry several trigger nodes — a run
    // is started by exactly one, and the others are pruned for that run
    // (see `pruneUnfiredTriggerBranches` in the Temporal graph helpers).
    additionalEventTypes: z
      .array(fallbackEventTypeSchema)
      .optional()
      .describe(
        "Additional event types that also trigger this workflow (OR with eventType).",
      ),
    // Per-event payload filters: an event triggers only if it passes every
    // filter scoped to it. A filter with `eventType` set applies only to that
    // event; without, it applies to all. Lets one trigger fire on e.g.
    // DRAFT_SALES_ORDER_READY (always) OR TASK_COMPLETED (only category=...).
    payloadFilters: z
      .array(
        z.object({
          eventType: z
            .string()
            .optional()
            .describe(
              "Apply this filter only to this event type; omit to apply to all.",
            ),
          field: z
            .string()
            .describe("Dot-path into eventData (e.g. 'category')."),
          equals: z.string().describe("Required value at `field`."),
        }),
      )
      .optional()
      .describe(
        "Per-event payload filters; an event triggers only if it passes all filters scoped to it.",
      ),
  }),
]);

export type EventTriggerConfig = z.infer<typeof configSchema>;

// Output schema - contains the event data
// Creates a discriminated union of all possible event schemas from SystemEventMap
const outputSchema = z.object({
  eventData: z.any(), // TODO hopefully one day this will be a discriminated union based on the eventType
});

// No emissions from trigger steps
const emits = [] as const;

export const eventTriggerStep = defineStepConfig({
  type: "event_trigger",
  name: "Event Trigger",
  description: "Entry point for workflows triggered by system events",
  category: "Trigger",
  inputSchema,
  outputSchema,
  configSchema,
  emits,
});
